/**
 * Recursive-descent parser: Token[] → Ast. Precedence (low→high):
 * comparison → additive → multiplicative → unary → primary.
 *
 * Throws `FormulaParseError` on malformed input; callers (the admin editor,
 * `parseFormula`) catch it to show inline validation.
 */
import { tokenize } from './tokenizer.js';
import { FUNCTIONS } from './types.js';
export class FormulaParseError extends Error {
}
const FN_SET = new Set(FUNCTIONS);
export function parse(src) {
    const toks = tokenize(src);
    let p = 0;
    const peek = () => toks[p];
    const next = () => toks[p++];
    const expect = (v) => {
        const t = next();
        if (!t || t.v !== v)
            throw new FormulaParseError(`expected '${v}'`);
    };
    function parseExpr() { return parseComparison(); }
    function parseComparison() {
        let left = parseAdditive();
        while (peek() && peek().t === 'op' && ['=', '<>', '<', '>', '<=', '>='].includes(peek().v)) {
            const op = next().v;
            left = { k: 'bin', op, left, right: parseAdditive() };
        }
        return left;
    }
    function parseAdditive() {
        let left = parseMul();
        while (peek() && peek().t === 'op' && (peek().v === '+' || peek().v === '-')) {
            const op = next().v;
            left = { k: 'bin', op, left, right: parseMul() };
        }
        return left;
    }
    function parseMul() {
        let left = parseUnary();
        while (peek() && peek().t === 'op' && (peek().v === '*' || peek().v === '/')) {
            const op = next().v;
            left = { k: 'bin', op, left, right: parseUnary() };
        }
        return left;
    }
    function parseUnary() {
        if (peek() && peek().t === 'op' && peek().v === '-') {
            next();
            return { k: 'neg', e: parseUnary() };
        }
        return parsePrimary();
    }
    function parsePrimary() {
        const t = next();
        if (!t)
            throw new FormulaParseError('unexpected end of formula');
        if (t.t === 'num')
            return { k: 'num', v: t.v };
        if (t.t === 'str')
            return { k: 'str', v: t.v };
        if (t.t === 'col')
            return { k: 'col', v: t.v };
        if (t.t === 'const')
            return { k: 'const', v: t.v };
        if (t.t === 'op' && t.v === '(') {
            const e = parseExpr();
            expect(')');
            return e;
        }
        if (t.t === 'id') {
            const upper = t.v.toUpperCase();
            if (peek() && peek().v === '(') { // function call
                next();
                if (!FN_SET.has(upper))
                    throw new FormulaParseError(`unsupported function '${t.v}'`);
                const args = [];
                if (peek() && peek().v !== ')') {
                    args.push(parseExpr());
                    while (peek() && peek().v === ',') {
                        next();
                        args.push(parseExpr());
                    }
                }
                expect(')');
                return { k: 'call', name: upper, args };
            }
            if (upper === 'TRUE')
                return { k: 'bool', v: true };
            if (upper === 'FALSE')
                return { k: 'bool', v: false };
            throw new FormulaParseError(`unexpected identifier '${t.v}'`);
        }
        throw new FormulaParseError(`unexpected token '${t.v}'`);
    }
    const ast = parseExpr();
    if (p < toks.length)
        throw new FormulaParseError(`unexpected trailing token '${toks[p].v}'`);
    return ast;
}
//# sourceMappingURL=parser.js.map